import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import Button from "../../Button";
import { Field, Fieldset } from "@headlessui/react";
import FormInput from "../../Form/FormInput";
import FormPhoneInput from "../../Form/FormPhoneInput";
import FormSelect from "../../Form/FormSelect";
import FormDate from "../../Form/FormDate";
import {
  addLocalNotification,
  clearBusinessEmployee,
  requestCreateBusinessEmployee,
  requestUpdateBusinessEmployee,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import {
  cancelAPIRequest,
  getWorkEndBounds,
  getWorkStartBounds,
  parseTimeString,
} from "../../../../Utils";
import FormError from "../../Form/FormError";
import FormRadioGroup from "../../Form/FormRadioGroup";
import DialogModal from "../../../DialogModal";
import { BANKS } from "../../../../Constants";
import { Link } from "react-router-dom";
import { CheckIcon, ExternalLinkIcon } from "@heroicons/react/outline";
import {
  DEPENDENT_OPTIONS,
  EMPLOYMENT_STATUS_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  ID_TYPE_OPTIONS,
  LATE_DEDUCTION_TYPE_OPTIONS,
  MARRIAGE_STATUS_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_PERIOD_OPTIONS,
  RISK_LEVEL_OPTIONS,
  SALARY_TYPE_OPTIONS,
  TAX_METHOD_OPTIONS,
} from "../../../../Constants/business";
import Divider from "../../Divider";
import LateDeductionComponent from "../../../LateDeductionComponent";
import FormLabel from "../../Form/FormLabel";

const FEATURE_RELEASED = false;

// A new employee starts on KTP, which nearly every employee has, so the ID
// number opens in the 16-digit NIK format instead of the 9-character passport one.
const NEW_EMPLOYEE_DEFAULTS = {
  idType: ID_TYPE_OPTIONS.find((option) => option.value === "KTP"),
};

const EmployeeModal = ({ open, onClose, onCreated, employee }) => {
  const { t, i18n } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(() =>
    employee ? {} : NEW_EMPLOYEE_DEFAULTS,
  );
  const [errors, setErrors] = useState({});

  const dispatch = useBusinessTransactionDispatch();

  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    request,
    newEmployee,
    newEmployeeLoading,
    newEmployeeError,
    updateEmployee,
    updateEmployeeLoading,
    updateEmployeeError,
  } = useBusinessTransactionState();
  const notificationDispatch = useNotificationDispatch();

  const isEdit = Boolean(employee);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const cleanedPhone = formData.phone?.replace(/\s/g, "");
  const today = new Date();

  const workStartBounds = React.useMemo(
    () =>
      getWorkStartBounds(
        formData.standardWorkStartTime,
        formData.standardWorkEndTime,
        15
      ),
    [formData.standardWorkStartTime, formData.standardWorkEndTime]
  );

  const workEndBounds = React.useMemo(
    () =>
      getWorkEndBounds(
        formData.standardWorkStartTime,
        formData.standardWorkEndTime,
        15
      ),
    [formData.standardWorkStartTime, formData.standardWorkEndTime]
  );

  // Clear errors on unmount
  useEffect(() => {
    return () => {
      if (request) {
        cancelAPIRequest(request);
      }
      clearBusinessEmployee(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Populate form when editing
  useEffect(() => {
    if (isEdit && employee?.id) {
      setFormData({
        firstName: employee.firstName || "",
        lastName: employee.lastName || "",
        gender:
          GENDER_OPTIONS.find((option) => option.value === employee.gender) ||
          null,
        marriageStatus:
          MARRIAGE_STATUS_OPTIONS.find(
            (option) => option.value === employee.marriageStatus
          ) || null,
        dependent:
          DEPENDENT_OPTIONS.find(
            (option) => option.value === employee.dependent
          ) || null,
        birthDate: employee.birthDate ? new Date(employee.birthDate) : null,
        birthPlace: employee.birthPlace || "",
        email: employee.email || "",
        phone: employee.phone || "",
        address: employee.address || "",

        idType:
          ID_TYPE_OPTIONS.find((option) => option.value === employee.idType) ||
          null,
        idNumber: employee.idNumber || "",
        npwp: employee.npwp || "",

        employeeId: employee.employeeID || "",
        employmentType:
          EMPLOYMENT_TYPE_OPTIONS.find(
            (option) => option.value === employee.employmentType
          ) || null,
        employmentStatus:
          EMPLOYMENT_STATUS_OPTIONS.find(
            (option) => option.value === employee.employmentStatus
          ) || null,
        riskLevel:
          RISK_LEVEL_OPTIONS.find(
            (option) => option.value === employee.riskLevel
          ) || null,
        startDate: employee.startDate ? new Date(employee.startDate) : null,
        endDate: employee.endDate ? new Date(employee.endDate) : null,
        taxSubjectStartDate: employee.taxSubjectStartDate
          ? new Date(employee.taxSubjectStartDate)
          : null,
        taxSubjectEndDate: employee.taxSubjectEndDate
          ? new Date(employee.taxSubjectEndDate)
          : null,
        department: employee.department || "",
        position: employee.position || "",

        salaryType:
          SALARY_TYPE_OPTIONS.find(
            (option) => option.value === employee.salaryType
          ) || null,
        baseSalary: employee.baseSalary ? employee.baseSalary.toString() : "",
        taxMethod:
          TAX_METHOD_OPTIONS.find(
            (option) => option.value === employee.taxMethod
          ) || null,
        paymentMethod:
          PAYMENT_METHOD_OPTIONS.find(
            (option) => option.value === employee.paymentMethod
          ) || null,
        paymentPeriod:
          PAYMENT_PERIOD_OPTIONS.find(
            (option) => option.value === employee.paymentPeriod
          ) || null,

        bankName:
          BANKS.find(
            (bank) =>
              bank.value === employee.bankAccount?.bankName ||
              String(bank.id) === String(employee.bankAccount?.bankName)
          ) || null,
        bankBranch: employee.bankAccount?.bankBranch || "",
        accountNumber: employee.bankAccount?.accountNumber || "",
        accountHolderName: employee.bankAccount?.accountHolderName || "",
        minimumWorkHoursPerDay: employee.minimumWorkHoursPerDay || "",
        standardWorkStartTime: employee.standardWorkStartTime
          ? parseTimeString(employee.standardWorkStartTime)
          : null,
        standardWorkEndTime: employee.standardWorkEndTime
          ? parseTimeString(employee.standardWorkEndTime)
          : null,
        lateThresholdMinutes: employee.lateThresholdMinutes || "",
        earlyLeaveThresholdMinutes: employee.earlyLeaveThresholdMinutes || "",
        lateDeductionEnabled: employee.lateDeductionEnabled || false,
        lateDeductionType: employee.lateDeductionType
          ? LATE_DEDUCTION_TYPE_OPTIONS.find(
              (opt) => opt.value === employee.lateDeductionType
            )
          : null,
        lateDeductionRate: employee.lateDeductionRate
          ? employee.lateDeductionRate.toString()
          : "",
        lateDeductionMultiplier: employee.lateDeductionMultiplier || "",
        lateDeductionBasedOnSalary:
          employee.lateDeductionBasedOnSalary || false,
        maxLateDeductionPerDay: employee.maxLateDeductionPerDay || "",
        lateDeductionTiers:
          employee.lateDeductionTiers?.length > 0
            ? employee.lateDeductionTiers.map((tier) => ({
                id: tier.id,
                fromMinutes: tier.fromMinutes,
                toMinutes: tier.toMinutes,
                deductionRate: tier.deductionRate
                  ? tier.deductionRate.toString()
                  : "",
              }))
            : [],
      });
    }
  }, [isEdit, employee]);

  useEffect(() => {
    if (newEmployee?.id) {
      // Close modal
      onClose();
      onCreated?.();

      // Reset form data
      setFormData({});
      setActiveStep(0);

      //clear business employee create
      clearBusinessEmployee(dispatch);

      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `add-employee-${newEmployee?.id}`,
        type: "success",
        title: "common.success",
        body: "employees.notifications.addSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, newEmployee?.id, dispatch, onClose, onCreated]);

  useEffect(() => {
    if (updateEmployee?.id) {
      // Close modal
      onClose();

      // Reset form data
      setFormData({});
      setActiveStep(0);

      //clear business employee create
      clearBusinessEmployee(dispatch);

      // Show success notification
      addLocalNotification(notificationDispatch, {
        id: `edit-employee-${updateEmployee?.id}`,
        type: "success",
        title: "common.success",
        body: "employees.notifications.updateSuccess",
        showFull: true,
      });
    }
  }, [notificationDispatch, updateEmployee?.id, dispatch, onClose]);

  const handleCleanup = () => {
    onClose();
    setFormData({});
    setErrors({});
    setActiveStep(0);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updates = { [field]: value };

      if (field === "firstName" || field === "lastName") {
        const firstName = field === "firstName" ? value : prev.firstName;
        const lastName = field === "lastName" ? value : prev.lastName;

        const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

        if (fullName) {
          updates.accountHolderName = fullName;
        }
      }

      // A number typed for one ID type is not valid for the other.
      if (field === "idType" && value?.value !== prev.idType?.value) {
        updates.idNumber = "";
      }

      return { ...prev, ...updates };
    });

    if (errors[field] || (field === "idType" && errors.idNumber)) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
        ...(field === "firstName" || field === "lastName"
          ? { accountHolderName: "" }
          : {}),
        ...(field === "idType" ? { idNumber: "" } : {}),
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = "employees.errors.firstNameRequired";
    }
    if (!formData.gender) {
      newErrors.gender = "employees.errors.genderRequired";
    }
    if (!formData.marriageStatus) {
      newErrors.marriageStatus = "employees.errors.marriageStatusRequired";
    }
    if (formData.birthDate) {
      const birthDate = new Date(formData.birthDate);
      if (birthDate > today) {
        newErrors.birthDate = "employees.errors.futureDate";
      }
    }
    if (!formData.email?.trim()) {
      newErrors.email = "employees.errors.emailRequired";
    }
    if (formData.email) {
      const isEmailValid = emailRegex.test(formData.email);
      if (!isEmailValid) {
        newErrors.email = "employees.errors.emailInvalid";
      }
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = "employees.errors.phoneRequired";
    }
    if (formData.phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (!isPhoneValid) {
        newErrors.phone = "employees.errors.phoneInvalid";
      }
    }

    if (!formData.idType) {
      newErrors.idType = "employees.errors.idTypeRequired";
    }
    if (!formData.idNumber?.trim()) {
      newErrors.idNumber = "employees.errors.idNumberRequired";
    }
    if (formData.npwp) {
      const isNpwpValid =
        formData.npwp.length >= 15 && formData.npwp.length <= 16;
      if (!isNpwpValid) {
        newErrors.npwp = "employees.errors.npwpInvalid";
      }
    }

    if (!formData.employeeId?.trim()) {
      newErrors.employeeId = "employees.errors.employeeIdRequired";
    }
    if (!formData.employmentType) {
      newErrors.employmentType = "employees.errors.employmentTypeRequired";
    }
    if (!formData.employmentStatus) {
      newErrors.employmentStatus = "employees.errors.employmentStatusRequired";
    }
    if (!formData.startDate) {
      newErrors.startDate = "employees.errors.startDateRequired";
    }
    if (formData.startDate) {
      const startDate = new Date(formData.startDate);
      if (startDate > today) {
        newErrors.startDate = "employees.errors.futureDate";
      }
    }
    if (formData.minimumWorkHoursPerDay) {
      const value = parseFloat(formData.minimumWorkHoursPerDay);
      if (isNaN(value) || value < 1) {
        newErrors.minimumWorkHoursPerDay = "employees.errors.minimumWorkHours";
      }
    }

    if (!formData.salaryType) {
      newErrors.salaryType = "employees.errors.salaryTypeRequired";
    }
    if (!formData.baseSalary?.trim()) {
      newErrors.baseSalary = "employees.errors.baseSalaryRequired";
    }
    if (!formData.taxMethod) {
      newErrors.taxMethod = "employees.errors.taxMethodRequired";
    }
    if (
      formData.taxSubjectStartDate &&
      formData.taxSubjectEndDate &&
      formData.taxSubjectEndDate < formData.taxSubjectStartDate
    ) {
      newErrors.taxSubjectEndDate = "employees.errors.taxSubjectEndBeforeStart";
    }
    if (!formData.paymentPeriod) {
      newErrors.paymentPeriod = "employees.errors.paymentPeriodRequired";
    }
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = "employees.errors.paymentMethodRequired";
    }
    if (formData.paymentMethod?.value === "BANK_TRANSFER") {
      if (!formData.bankName) {
        newErrors.bankName = "employees.errors.bankNameRequired";
      }
      if (!formData.bankBranch?.trim()) {
        newErrors.bankBranch = "employees.errors.bankBranchRequired";
      }
      if (!formData.accountNumber?.trim()) {
        newErrors.accountNumber = "employees.errors.accountNumberRequired";
      }
      if (!formData.accountHolderName?.trim()) {
        newErrors.accountHolderName =
          "employees.errors.accountHolderNameRequired";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateTiers = () => {
    const errors = [];
    const tiers = formData.lateDeductionTiers || [];

    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];

      if (tier.fromMinutes >= tier.toMinutes) {
        errors.push(
          t("employees.lateDeduction.tiers.errors.endTimeGreaterThanStart", {
            tier: i + 1,
          })
        );
      }

      if (i > 0 && tier.fromMinutes <= tiers[i - 1].toMinutes) {
        errors.push(
          t(
            "employees.lateDeduction.tiers.errors.startGreaterThanPreviousEnd",
            {
              tier: i + 1,
            }
          )
        );
      }

      if (tier.deductionRate <= 0 || tier.deductionRate > 100) {
        errors.push(
          t("employees.lateDeduction.tiers.errors.deductionRateRange", {
            tier: i + 1,
          })
        );
      }
    }

    return errors;
  };

  const handleNext = (e) => {
    e.preventDefault();
    if (activeStep === steps.length - 1) {
      if (validateForm() && validateTiers().length === 0) {
        handleSubmit();
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      const hasFilledData = Object.keys(formData).some((key) => {
        const value = formData[key];
        if (value === NEW_EMPLOYEE_DEFAULTS[key]) return false;
        return value !== null && value !== undefined && value !== "";
      });

      if (hasFilledData) {
        setShowConfirmDialog(true);
      } else {
        handleCleanup();
      }
    } else {
      setActiveStep((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    const input = {
      businessID: user.mainBusiness.id,
      firstName: formData.firstName,
      lastName: formData.lastName || null,
      gender: formData.gender?.value,
      marriageStatus: formData.marriageStatus?.value,
      dependent: formData.dependent?.value || null,
      birthDate: formData.birthDate,
      birthPlace: formData.birthPlace,
      email: formData.email,
      phone: cleanedPhone,
      address: formData.address,
      idType: formData.idType?.value,
      idNumber: formData.idNumber,
      npwp: formData.npwp || null,
      employeeID: formData.employeeId,
      startDate: formData.startDate,
      endDate: formData.endDate || null,
      employmentStatus: formData.employmentStatus?.value,
      employmentType: formData.employmentType?.value,
      department: formData.department || null,
      position: formData.position || null,
      salaryType: formData.salaryType?.value,
      baseSalary: Number(formData.baseSalary?.replace(/\D/g, "")),
      taxMethod: formData.taxMethod?.value,
      taxSubjectStartDate: formData.taxSubjectStartDate || null,
      taxSubjectEndDate: formData.taxSubjectEndDate || null,
      riskLevel: formData.riskLevel?.value || null,
      paymentMethod: formData.paymentMethod?.value,
      paymentPeriod: formData.paymentPeriod?.value,
      bankAccount:
        formData.paymentMethod?.value === "BANK_TRANSFER"
          ? {
              bankName: formData.bankName?.value,
              bankBranch: formData.bankBranch,
              accountNumber: formData.accountNumber,
              accountHolderName: formData.accountHolderName,
            }
          : null,
      minimumWorkHoursPerDay: formData.minimumWorkHoursPerDay
        ? parseFloat(formData.minimumWorkHoursPerDay)
        : null,
      standardWorkStartTime: formData.standardWorkStartTime
        ? new Date(formData.standardWorkStartTime).toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        : null,
      standardWorkEndTime: formData.standardWorkEndTime
        ? new Date(formData.standardWorkEndTime).toLocaleTimeString("en-US", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })
        : null,
      lateThresholdMinutes: formData.lateThresholdMinutes
        ? parseInt(formData.lateThresholdMinutes)
        : null,
      earlyLeaveThresholdMinutes: formData.earlyLeaveThresholdMinutes
        ? parseInt(formData.earlyLeaveThresholdMinutes)
        : null,
      lateDeductionEnabled: formData.lateDeductionEnabled || false,
      lateDeductionType: formData.lateDeductionType?.value || null,
      lateDeductionRate: formData.lateDeductionRate
        ? Number(String(formData.lateDeductionRate).replace(/\D/g, ""))
        : null,
      lateDeductionMultiplier: formData.lateDeductionMultiplier
        ? parseFloat(formData.lateDeductionMultiplier)
        : null,
      lateDeductionBasedOnSalary: formData.lateDeductionBasedOnSalary || false,
      maxLateDeductionPerDay: formData.maxLateDeductionPerDay
        ? parseFloat(formData.maxLateDeductionPerDay)
        : null,
      lateDeductionTiers:
        formData.lateDeductionType?.value === "TIERED"
          ? formData.lateDeductionTiers?.map((tier) => ({
              id: tier.id || null,
              fromMinutes: tier.fromMinutes,
              toMinutes: tier.toMinutes,
              deductionRate: tier.deductionRate,
            }))
          : null,
    };

    if (isEdit) {
      input.id = employee.id;
      requestUpdateBusinessEmployee(dispatch, { input });
    } else {
      requestCreateBusinessEmployee(dispatch, { input });
    }
  };

  const getDefaultBirthDate = () => {
    return new Date(
      today.getFullYear() - 17,
      today.getMonth(),
      today.getDate()
    );
  };

  const renderPersonalInfo = () => {
    return (
      <div className="space-y-4">
        <h3 className="text-base font-medium text-gray-900 mb-2">
          {t("employees.modal.steps.personal")}
        </h3>
        <Field>
          <FormInput
            required
            label="employees.fields.firstName"
            id="firstName"
            name="firstName"
            value={formData.firstName || ""}
            onChange={(e) => handleChange("firstName", e.target.value)}
            placeholder={t("employees.placeholders.firstName")}
            error={errors.firstName}
          />
        </Field>
        <Field>
          <FormInput
            label="employees.fields.lastName"
            id="lastName"
            name="lastName"
            value={formData.lastName || ""}
            onChange={(e) => handleChange("lastName", e.target.value)}
            placeholder={t("employees.placeholders.lastName")}
            hint={t("common.optional")}
          />
        </Field>
        <Field>
          <FormRadioGroup
            required
            label="employees.fields.gender"
            id="gender"
            name="gender"
            options={GENDER_OPTIONS}
            value={formData.gender || null}
            onChange={(option) => handleChange("gender", option)}
            gridClassName="grid-cols-1 sm:grid-cols-2"
            error={errors.gender}
          />
        </Field>
        <Field>
          <FormSelect
            required
            label="employees.fields.marriageStatus"
            id="marriageStatus"
            name="marriageStatus"
            value={formData.marriageStatus || null}
            options={MARRIAGE_STATUS_OPTIONS}
            onChange={(option) => handleChange("marriageStatus", option)}
            placeholder={t("employees.placeholders.marriageStatus")}
            error={errors.marriageStatus}
          />
        </Field>
        <Field>
          <FormSelect
            label="employees.fields.dependent"
            id="dependent"
            name="dependent"
            value={formData.dependent || null}
            options={DEPENDENT_OPTIONS}
            onChange={(option) => handleChange("dependent", option)}
            placeholder={t("employees.placeholders.dependent")}
            hint={t("common.optional")}
            onClear={() => handleChange("dependent", null)}
          />
        </Field>
        <Field>
          <FormDate
            label="employees.fields.birthDate"
            name="birthDate"
            selected={formData.birthDate || null}
            // Only where the calendar opens: showing it as the value made an
            // empty field look filled while nothing was saved.
            openToDate={formData.birthDate ? undefined : getDefaultBirthDate()}
            onChange={(option) => handleChange("birthDate", option)}
            dateFormat="dd/MM/yyyy"
            placeholderText={t("employees.placeholders.birthDate")}
            error={errors.birthDate}
            hint={t("common.optional")}
            showMonthDropdown
            useShortMonthInDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={70}
            maxDate={today}
          />
        </Field>
        <Field>
          <FormInput
            label="employees.fields.birthPlace"
            name="birthPlace"
            value={formData.birthPlace || ""}
            onChange={(e) => handleChange("birthPlace", e.target.value)}
            placeholder={t("employees.placeholders.birthPlace")}
            hint={t("common.optional")}
          />
        </Field>
        <Field>
          <FormInput
            required
            label="employees.fields.email"
            name="email"
            value={formData.email || ""}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder={t("employees.placeholders.email")}
            error={errors.email}
            type="email"
          />
        </Field>
        <Field>
          <FormPhoneInput
            required
            label="employees.fields.phone"
            name="phone"
            value={formData.phone || ""}
            onChange={(value) => handleChange("phone", value)}
            placeholder={t("employees.placeholders.phone")}
            error={errors.phone}
          />
        </Field>
        <Field>
          <FormInput
            label="employees.fields.address"
            name="address"
            value={formData.address || ""}
            onChange={(e) => handleChange("address", e.target.value)}
            placeholder={t("employees.placeholders.address")}
            hint={t("common.optional")}
          />
        </Field>
      </div>
    );
  };

  const renderIdentityInfo = () => (
    <div className="space-y-4">
      <h3 className="text-base font-medium text-gray-900 mb-2">
        {t("employees.modal.steps.identity")}
      </h3>
      <Field>
        <FormSelect
          required
          label="employees.fields.idType"
          id="idType"
          name="idType"
          value={formData.idType || null}
          options={ID_TYPE_OPTIONS}
          onChange={(option) => handleChange("idType", option)}
          placeholder={t("employees.placeholders.idType")}
          error={errors.idType}
        />
      </Field>
      {formData.idType?.value === "KTP" ? (
        <Field>
          <FormInput
            label="employees.fields.idNumber"
            id="idNumber"
            name="idNumber"
            value={formData.idNumber || ""}
            onChange={(e) => handleChange("idNumber", e.target.value)}
            placeholder={t("employees.placeholders.idNumber")}
            error={errors.idNumber}
            allowLeadingZeros
            numberFormat
            decimalScale={0}
            isAllowed={({ value }) => value.length <= 16}
            allowNegative={false}
            required
          />
        </Field>
      ) : (
        <Field>
          <FormInput
            label="employees.fields.idNumber"
            id="idNumber"
            name="idNumber"
            value={formData.idNumber || ""}
            onChange={(e) => handleChange("idNumber", e.target.value)}
            placeholder={t("employees.placeholders.idNumber")}
            error={errors.idNumber}
            maxLength={9}
            required
          />
        </Field>
      )}
      <Field>
        <FormInput
          label="employees.fields.npwp"
          id="npwp"
          name="npwp"
          value={formData.npwp || ""}
          onChange={(e) => handleChange("npwp", e.target.value)}
          placeholder={t("employees.placeholders.npwp")}
          hint={t("common.optional")}
          error={errors.npwp}
          allowLeadingZeros
          numberFormat
          decimalScale={0}
          isAllowed={({ value }) => value.length <= 16}
          allowNegative={false}
        />
      </Field>
    </div>
  );

  const renderJobInfo = () => (
    <div className="space-y-4">
      <h3 className="text-base font-medium text-gray-900 mb-2">
        {t("employees.modal.steps.job")}
      </h3>
      <Field>
        <FormInput
          required
          label="employees.fields.employeeId"
          id="employeeId"
          name="employeeId"
          value={formData.employeeId || ""}
          onChange={(e) => handleChange("employeeId", e.target.value)}
          placeholder={t("employees.placeholders.employeeId")}
          error={errors.employeeId}
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field>
          <FormSelect
            required
            label="employees.fields.employmentType"
            id="employmentType"
            name="employmentType"
            value={formData.employmentType || null}
            options={EMPLOYMENT_TYPE_OPTIONS}
            onChange={(option) => handleChange("employmentType", option)}
            placeholder={t("employees.placeholders.employmentType")}
            error={errors.employmentType}
          />
        </Field>
        <Field>
          <FormSelect
            required
            label="employees.fields.employmentStatus"
            id="employmentStatus"
            name="employmentStatus"
            value={formData.employmentStatus || null}
            options={EMPLOYMENT_STATUS_OPTIONS}
            onChange={(option) => handleChange("employmentStatus", option)}
            placeholder={t("employees.placeholders.employmentStatus")}
            error={errors.employmentStatus}
          />
        </Field>
      </div>
      <Field>
        <FormSelect
          label={t("employees.fields.riskLevel")}
          id="riskLevel"
          name="riskLevel"
          value={formData.riskLevel || null}
          options={RISK_LEVEL_OPTIONS}
          onChange={(option) => handleChange("riskLevel", option)}
          placeholder={t("employees.placeholders.riskLevel")}
          hint={t("common.optional")}
          onClear={() => handleChange("riskLevel", null)}
        />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field>
          <FormDate
            required
            label="employees.fields.startDate"
            name="startDate"
            selected={formData.startDate || null}
            onChange={(option) => handleChange("startDate", option)}
            dateFormat="dd/MM/yyyy"
            placeholderText={t("employees.placeholders.startDate")}
            error={errors.startDate}
            showMonthDropdown
            useShortMonthInDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={70}
            maxDate={today}
          />
        </Field>
        <Field>
          <FormDate
            label="employees.fields.endDate"
            name="endDate"
            selected={formData.endDate || null}
            onChange={(option) => handleChange("endDate", option)}
            dateFormat="dd/MM/yyyy"
            placeholderText={t("employees.placeholders.endDate")}
            onClear={() => handleChange("endDate", null)}
            error={errors.endDate}
            showMonthDropdown
            useShortMonthInDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={70}
            minDate={
              formData.startDate
                ? new Date(formData.startDate.getTime() + 86400000)
                : today
            }
            hint={t("common.optional")}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field>
          <FormInput
            label="employees.fields.department"
            id="department"
            name="department"
            value={formData.department || ""}
            onChange={(e) => handleChange("department", e.target.value)}
            placeholder={t("employees.placeholders.department")}
            hint={t("common.optional")}
          />
        </Field>
        <Field>
          <FormInput
            label="employees.fields.position"
            id="position"
            name="position"
            value={formData.position || ""}
            onChange={(e) => handleChange("position", e.target.value)}
            placeholder={t("employees.placeholders.position")}
            hint={t("common.optional")}
          />
        </Field>
      </div>
      <Divider />
      <Field>
        <FormInput
          hint={t("common.optional")}
          label="employees.fields.minimumWorkHoursPerDay"
          id="minimumWorkHoursPerDay"
          name="minimumWorkHoursPerDay"
          value={formData.minimumWorkHoursPerDay || ""}
          type="number"
          onChange={(e) =>
            handleChange("minimumWorkHoursPerDay", e.target.value)
          }
          numberFormat
          placeholder={t("employees.placeholders.minimumWorkHoursPerDay")}
          isAllowed={({ value }) => value.length <= 3}
          allowNegative={false}
          error={errors.minimumWorkHoursPerDay}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field>
          <FormDate
            hint={t("common.optional")}
            label="employees.fields.standardWorkStartTime"
            id="standardWorkStartTime"
            name="standardWorkStartTime"
            selected={formData.standardWorkStartTime || null}
            onChange={(option) => handleChange("standardWorkStartTime", option)}
            placeholderText={t("employees.placeholders.standardWorkStartTime")}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="HH:mm"
            timeFormat="HH:mm"
            onClear={() => handleChange("standardWorkStartTime", null)}
            minTime={workStartBounds.minTime}
            maxTime={workStartBounds.maxTime}
          />
        </Field>
        <Field>
          <FormDate
            hint={t("common.optional")}
            label="employees.fields.standardWorkEndTime"
            id="standardWorkEndTime"
            name="standardWorkEndTime"
            selected={formData.standardWorkEndTime || null}
            onChange={(option) => handleChange("standardWorkEndTime", option)}
            placeholderText={t("employees.placeholders.standardWorkEndTime")}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Time"
            dateFormat="HH:mm"
            timeFormat="HH:mm"
            onClear={() => handleChange("standardWorkEndTime", null)}
            minTime={workEndBounds.minTime}
            maxTime={workEndBounds.maxTime}
            error={errors.standardWorkEndTime}
          />
        </Field>
      </div>

      {FEATURE_RELEASED && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <FormLabel
                htmlFor="lateThresholdMinutes"
                hint={"common.optional"}
              >
                {t("employees.fields.lateThresholdMinutes")}
              </FormLabel>
              <Field className="mt-2">
                <FormInput
                  id="lateThresholdMinutes"
                  name="lateThresholdMinutes"
                  value={formData.lateThresholdMinutes || ""}
                  onChange={(e) =>
                    handleChange("lateThresholdMinutes", e.target.value)
                  }
                  placeholder={t("employees.placeholders.lateThresholdMinutes")}
                  numberFormat
                  isAllowed={({ value }) => value >= 0 && value <= 1440}
                />
              </Field>
            </div>
            <div>
              <FormLabel
                htmlFor="earlyLeaveThresholdMinutes"
                hint={"common.optional"}
              >
                {t("employees.fields.earlyLeaveThresholdMinutes")}
              </FormLabel>
              <Field
                className={i18n.language === "id" ? "mt-2 sm:mt-7" : "mt-2"}
              >
                <FormInput
                  id="earlyLeaveThresholdMinutes"
                  name="earlyLeaveThresholdMinutes"
                  value={formData.earlyLeaveThresholdMinutes || ""}
                  onChange={(e) =>
                    handleChange("earlyLeaveThresholdMinutes", e.target.value)
                  }
                  numberFormat
                  placeholder={t(
                    "employees.placeholders.earlyLeaveThresholdMinutes"
                  )}
                  isAllowed={({ value }) => value >= 0 && value <= 1440}
                />
              </Field>
            </div>
          </div>

          <Divider />

          <LateDeductionComponent
            formData={formData}
            handleChange={handleChange}
            options={LATE_DEDUCTION_TYPE_OPTIONS}
            validateTiers={validateTiers}
          />
        </>
      )}
    </div>
  );

  const renderSalaryInfo = () => (
    <div className="space-y-4">
      <h3 className="text-base font-medium text-gray-900 mb-2">
        {t("employees.salary.title")}
      </h3>
      <Field>
        <FormSelect
          required
          label="employees.fields.salaryType"
          id="salaryType"
          name="salaryType"
          value={formData.salaryType || null}
          options={SALARY_TYPE_OPTIONS}
          onChange={(option) => handleChange("salaryType", option)}
          placeholder={t("employees.placeholders.salaryType")}
          error={errors.salaryType}
        />
      </Field>
      <Field>
        <FormInput
          required
          label="employees.fields.baseSalary"
          id="baseSalary"
          name="baseSalary"
          value={formData.baseSalary || ""}
          onChange={(e) => handleChange("baseSalary", e.target.value)}
          placeholder={t("employees.placeholders.baseSalary")}
          error={errors.baseSalary}
          numberFormat={{
            thousandSeparator: true,
          }}
          prefix="Rp"
          allowNegative={false}
        />
      </Field>
      <Field>
        <FormSelect
          required
          label="employees.fields.taxMethod"
          id="taxMethod"
          name="taxMethod"
          value={formData.taxMethod || null}
          options={TAX_METHOD_OPTIONS}
          onChange={(option) => handleChange("taxMethod", option)}
          placeholder={t("employees.placeholders.taxMethod")}
          error={errors.taxMethod}
        />
      </Field>
      <Field className="sm:col-span-2">
        <p className="text-sm font-medium text-gray-900">
          {t("employees.fields.taxSubjectPeriod")}
        </p>
        <p className="mt-1 text-sm text-gray-500">
          {t("employees.fields.taxSubjectPeriodHelp")}
        </p>
      </Field>
      <Field>
        <FormDate
          label="employees.fields.taxSubjectStartDate"
          name="taxSubjectStartDate"
          selected={formData.taxSubjectStartDate || null}
          onChange={(option) => handleChange("taxSubjectStartDate", option)}
          dateFormat="dd/MM/yyyy"
          placeholderText={t("employees.placeholders.taxSubjectStartDate")}
          onClear={() => handleChange("taxSubjectStartDate", null)}
          showMonthDropdown
          useShortMonthInDropdown
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={70}
        />
      </Field>
      <Field>
        <FormDate
          label="employees.fields.taxSubjectEndDate"
          name="taxSubjectEndDate"
          selected={formData.taxSubjectEndDate || null}
          onChange={(option) => handleChange("taxSubjectEndDate", option)}
          dateFormat="dd/MM/yyyy"
          placeholderText={t("employees.placeholders.taxSubjectEndDate")}
          onClear={() => handleChange("taxSubjectEndDate", null)}
          error={errors.taxSubjectEndDate}
          showMonthDropdown
          useShortMonthInDropdown
          showYearDropdown
          scrollableYearDropdown
          yearDropdownItemNumber={70}
          minDate={formData.taxSubjectStartDate || undefined}
        />
      </Field>
      <Field>
        <FormSelect
          required
          label="employees.fields.paymentMethod"
          id="paymentMethod"
          name="paymentMethod"
          value={formData.paymentMethod || null}
          options={PAYMENT_METHOD_OPTIONS}
          onChange={(option) => handleChange("paymentMethod", option)}
          placeholder={t("employees.placeholders.paymentMethod")}
          error={errors.paymentMethod}
        />
      </Field>
      {formData.paymentMethod?.value === "BANK_TRANSFER" && (
        <Fieldset className="border-y pb-4 pt-1.5 space-y-4">
          <Field>
            <FormSelect
              required
              label="employees.fields.bankName"
              searchable
              id="bankName"
              name="bankName"
              value={formData.bankName || null}
              options={BANKS.map((option) => ({
                label: `${option.shortLabel} (${option.code}) - ${option.abbreviation}`,
                value: option.value,
              }))}
              onChange={(option) => handleChange("bankName", option)}
              placeholder={t("employees.placeholders.bankName")}
              error={errors.bankName}
              isClearable
            />
          </Field>
          <Field>
            <FormInput
              required
              label="employees.fields.bankBranch"
              id="bankBranch"
              name="bankBranch"
              value={formData.bankBranch || ""}
              onChange={(e) => handleChange("bankBranch", e.target.value)}
              placeholder={t("employees.placeholders.bankBranch")}
              error={errors.bankBranch}
            />
          </Field>
          <Field>
            <FormInput
              required
              label="employees.fields.accountNumber"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber || ""}
              onChange={(e) => handleChange("accountNumber", e.target.value)}
              placeholder={t("employees.placeholders.accountNumber")}
              error={errors.accountNumber}
              allowNegative={false}
              numberFormat
              allowLeadingZeros
            />
          </Field>
          <Field>
            <FormInput
              required
              label="employees.fields.accountHolderName"
              id="accountHolderName"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={(e) =>
                handleChange("accountHolderName", e.target.value)
              }
              placeholder={t("employees.placeholders.accountHolderName")}
              error={errors.accountHolderName}
            />
          </Field>
        </Fieldset>
      )}
      <Field>
        <FormSelect
          required
          label="employees.fields.paymentPeriod"
          id="paymentPeriod"
          name="paymentPeriod"
          value={formData.paymentPeriod || null}
          options={PAYMENT_PERIOD_OPTIONS}
          onChange={(option) => handleChange("paymentPeriod", option)}
          placeholder={t("employees.placeholders.paymentPeriod")}
          error={errors.paymentPeriod}
        />
      </Field>
    </div>
  );

  const hasStepErrors = (stepName) => {
    switch (stepName) {
      case "personalInfo":
        return [
          "firstName",
          "gender",
          "birthDate",
          "birthPlace",
          "marriageStatus",
          "email",
          "phone",
        ].some((field) => errors[field]);
      case "identityInfo":
        return ["idType", "idNumber", "npwp"].some((field) => errors[field]);
      case "jobInfo":
        const tierErrors =
          formData.lateDeductionEnabled &&
          formData.lateDeductionType?.value === "TIERED"
            ? validateTiers()
            : [];

        return (
          [
            "employeeId",
            "employmentType",
            "employmentStatus",
            "startDate",
            "minimumWorkHoursPerDay",
          ].some((field) => errors[field]) || tierErrors.length > 0
        );
      case "salaryInfo":
        return [
          "salaryType",
          "baseSalary",
          "taxMethod",
          "taxSubjectEndDate",
          "paymentMethod",
          "paymentPeriod",
          "bankName",
          "bankBranch",
          "accountNumber",
          "accountHolderName",
        ].some((field) => errors[field]);
      default:
        return false;
    }
  };

  const validateStepFields = (stepName) => {
    switch (stepName) {
      case "personalInfo":
        return (
          formData.firstName?.trim() &&
          formData.gender &&
          formData.marriageStatus &&
          formData.email?.trim() &&
          emailRegex.test(formData.email) &&
          formData.phone?.trim() &&
          cleanedPhone?.length >= 10 &&
          (!formData.birthDate || formData.birthDate <= today)
        );

      case "identityInfo":
        return (
          formData.idType &&
          formData.idNumber?.trim() &&
          (!formData.npwp ||
            (formData.npwp.length >= 15 && formData.npwp.length <= 16))
        );

      case "jobInfo":
        const tierErrors =
          formData.lateDeductionEnabled &&
          formData.lateDeductionType?.value === "TIERED"
            ? validateTiers()
            : [];

        const minHoursValid =
          !formData.minimumWorkHoursPerDay ||
          (!isNaN(parseFloat(formData.minimumWorkHoursPerDay)) &&
            parseFloat(formData.minimumWorkHoursPerDay) >= 1);

        return (
          formData.employeeId?.trim() &&
          formData.employmentType &&
          formData.employmentStatus &&
          formData.startDate &&
          formData.startDate <= today &&
          tierErrors.length === 0 &&
          minHoursValid
        );

      case "salaryInfo":
        const bankTransferValid =
          formData.paymentMethod?.value === "BANK_TRANSFER"
            ? formData.bankName &&
              formData.bankBranch?.trim() &&
              formData.accountNumber?.trim() &&
              formData.accountHolderName?.trim()
            : true;

        return (
          formData.salaryType &&
          formData.baseSalary?.trim() &&
          formData.taxMethod &&
          formData.paymentMethod &&
          formData.paymentPeriod &&
          bankTransferValid
        );

      default:
        return false;
    }
  };

  const steps = [
    {
      name: "personalInfo",
      title: t("employees.modal.steps.personal"),
      component: renderPersonalInfo,
    },
    {
      name: "identityInfo",
      title: t("employees.modal.steps.identity"),
      component: renderIdentityInfo,
    },
    {
      name: "jobInfo",
      title: t("employees.modal.steps.job"),
      component: renderJobInfo,
    },
    {
      name: "salaryInfo",
      title: t("employees.modal.steps.salary"),
      component: renderSalaryInfo,
    },
  ];

  return (
    <SlideOver
      open={open}
      onClose={handleCleanup}
      title={
        isEdit ? "employees.modal.title.edit" : "employees.modal.title.add"
      }
      actionButton={
        <Link
          to={
            isEdit ? `/employee/create?id=${employee?.id}` : "/employee/create"
          }
          className="rounded-md bg-white text-gray-400 hover:text-gray-500"
          target="_blank"
          rel="noopener noreferrer"
        >
          <ExternalLinkIcon className="h-6 w-6" />
        </Link>
      }
      footer={
        <div className="flex justify-between items-center">
          <Button type="button" onClick={handleBack} variant="secondary">
            {activeStep === 0 ? t("common.cancel") : t("common.back")}
          </Button>
          <Button
            type="submit"
            form="employee-form"
            variant="primary"
            loading={newEmployeeLoading || updateEmployeeLoading}
            disabled={
              writeBlocked || newEmployeeLoading || updateEmployeeLoading
            }
          >
            {activeStep === steps.length - 1
              ? t("common.save")
              : t("common.next")}
          </Button>
        </div>
      }
    >
      <form id="employee-form" className="space-y-4 mb-6" onSubmit={handleNext}>
        <WriteBlockGate>
        {/* Steps indicator */}
        <nav className="flex justify-center">
          {steps.map((step, index) => {
            const hasError = hasStepErrors(step.name);
            const isValid = validateStepFields(step.name);
            const isCompleted = index < activeStep && isValid;
            const isActive = index === activeStep;

            return (
              <div
                key={step.title}
                className={`flex items-center ${
                  index !== steps.length - 1 ? "flex-1" : ""
                }`}
              >
                <button
                  type="button"
                  onClick={() => setActiveStep(index)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors border-2
            ${
              hasError
                ? "bg-red-600 text-white border-red-600"
                : isActive
                ? "bg-white text-blue-600 border-blue-600"
                : isCompleted
                ? "bg-blue-600 text-white border-blue-600"
                : "bg-white text-gray-400 border-gray-300"
            }
          `}
                >
                  {hasError ? (
                    "!"
                  ) : isCompleted ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    index + 1
                  )}
                </button>
                {index !== steps.length - 1 && (
                  <div
                    className={`h-0.5 flex-1 mx-2 transition-colors ${
                      hasError
                        ? "bg-red-600"
                        : isCompleted
                        ? "bg-blue-600"
                        : "bg-gray-200"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </nav>

        <FormError error={newEmployeeError || updateEmployeeError} />

        {/* Form content */}
        <Fieldset
          disabled={newEmployeeLoading || updateEmployeeLoading}
          className="data-[disabled]:opacity-50"
        >
          {steps[activeStep].component}
        </Fieldset>
        </WriteBlockGate>
      </form>

      {/* Confirmation dialog */}
      <DialogModal
        show={showConfirmDialog}
        title="common.confirmCancel"
        body="employees.modal.description.confirmCancel"
        onClose={() => setShowConfirmDialog(false)}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleCleanup}
      />
    </SlideOver>
  );
};

export default EmployeeModal;
